/* istanbul ignore file */
import React, { useEffect, useRef } from 'react';
import { cx as classNames } from '@linaria/core';
import { isNil } from 'lodash';
import { isPolygonShapeProps } from 'utils/typeguards/predictionOutputs';
import { thumbWrapper } from 'components/Canvas/RegionThumb/RegionThumb.styles';
import { RegionThumbProps } from 'components/Canvas/RegionThumb/common';
import { canvas, canvasContainer } from 'components/Canvas/RegionThumb/PolygonRegionThumb.styles';

export const PolygonRegionThumb: React.FC<React.PropsWithChildren<RegionThumbProps>> = ({ regionData, children, className, imageData, id, size }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement & { width: number; height: number }>(null);

  useEffect(() => {
    if (regionData && isPolygonShapeProps(regionData) && canvasRef.current && imageData && ('image' in imageData || imageData.video)) {
      const { current: canvasNode } = canvasRef;
      const { points } = regionData.polygon;
      const { width: naturalWidth, height: naturalHeight } = imageData;
      const context = canvasNode?.getContext('2d');

      canvasNode.width = naturalWidth;
      canvasNode.height = naturalHeight;

      if (context && points.length) {
        const smallestXCoord = Math.min(...points.map((p) => p.col));
        const smallestYCoord = Math.min(...points.map((p) => p.row));

        const greatestXCoord = Math.max(...points.map((p) => p.col));
        const greatestYCoord = Math.max(...points.map((p) => p.row));

        const distanceX = greatestXCoord - smallestXCoord;
        const distanceY = greatestYCoord - smallestYCoord;

        context.drawImage(
          'image' in imageData ? imageData.image : imageData.video,
          smallestXCoord * naturalWidth,
          smallestYCoord * naturalHeight,
          distanceX * naturalWidth,
          distanceY * naturalHeight,
          0,
          0,
          naturalWidth,
          naturalHeight,
        );
      }
    }
  }, [regionData, imageData]);

  if (!regionData || !imageData || !isPolygonShapeProps(regionData)) {
    return null;
  }

  return (
    <div ref={containerRef} className={classNames('region-thumb', thumbWrapper, canvasContainer, className)} role="presentation">
      {children}
      <canvas
        className={canvas}
        data-testid={`region-thumb-${id}`}
        ref={canvasRef}
        {...(!isNil(size) && {
          style: {
            height: size,
            width: size,
          },
        })}
      />
    </div>
  );
};
